import { useCurrency } from '@/hooks/useCurrency'
import { DataTable, ReportPage, StatGrid, useReport, exportCsv, type Column } from '@/components/reports/ReportKit'

interface RiderRow {
  rider: string
  deliveries: number
  value: number
  cashCollected: number
  avgOrder: number
}
interface DeliveryData {
  riders: RiderRow[]
  summary: { riderCount: number; deliveries: number; value: number; cashCollected: number }
}

export default function DeliveryPerformanceScreen() {
  const { formatCurrency } = useCurrency()
  const report = useReport<DeliveryData>('delivery-performance')
  const data = report.data

  const columns: Column<RiderRow>[] = [
    { key: 'rider', header: 'Rider', render: r => <span className="font-medium">{r.rider}</span> },
    { key: 'deliveries', header: 'Deliveries', align: 'right' },
    { key: 'value', header: 'Value handled', align: 'right', render: r => formatCurrency(r.value) },
    { key: 'avgOrder', header: 'Avg order', align: 'right', render: r => formatCurrency(r.avgOrder) },
    { key: 'cashCollected', header: 'Cash collected', align: 'right', render: r => <span className="font-semibold">{formatCurrency(r.cashCollected)}</span> }
  ]

  return (
    <ReportPage
      eyebrow="Delivery · Riders"
      title="Delivery Performance"
      subtitle="Deliveries, value handled and cash collected per rider — the cash each rider should hand in"
      report={report}
      onExport={() => exportCsv('delivery-performance', columns, data?.riders ?? [])}
    >
      {data && (
        <>
          <StatGrid
            stats={[
              { label: 'Riders', value: data.summary.riderCount },
              { label: 'Deliveries', value: data.summary.deliveries },
              { label: 'Value handled', value: formatCurrency(data.summary.value) },
              { label: 'Cash to collect', value: formatCurrency(data.summary.cashCollected), highlight: data.summary.cashCollected > 0 ? 'warn' : undefined }
            ]}
          />
          <DataTable
            title="By rider"
            columns={columns}
            rows={data.riders}
            rowKey={r => r.rider}
            defaultSort="deliveries"
            csvName="delivery-performance"
            empty="No deliveries with a rider assigned in this period."
          />
        </>
      )}
    </ReportPage>
  )
}
